"use server";

import { AuthError } from "next-auth";
import { headers } from "next/headers";

import { signIn } from "@/auth";
import { rateLimit } from "@/lib/rate-limit";
import { sendVerificationEmail } from "@/server/auth/email-verification";
import { registerUser, sendWelcomeEmail } from "@/server/auth/register";

export type RegisterState = { error: string } | null;

const str = (v: FormDataEntryValue | null) => (typeof v === "string" ? v : "");

export async function register(
  _prev: RegisterState,
  formData: FormData,
): Promise<RegisterState> {
  const email = str(formData.get("email")).trim().toLowerCase();
  const password = str(formData.get("password"));
  const requestHeaders = await headers();
  const clientIp = requestHeaders.get("x-forwarded-for")?.split(",").at(-1)?.trim()
    ?? requestHeaders.get("x-real-ip")
    ?? "unknown";
  if (!(await rateLimit(`auth:register:${clientIp}`, 5, 60 * 60))) {
    return { error: "Too many registration attempts. Please try again later." };
  }

  const result = await registerUser({
    name: str(formData.get("name")),
    handle: str(formData.get("handle")),
    role: str(formData.get("role")),
    email,
    password,
    passwordConfirmation: str(formData.get("password_confirmation")),
    inviteCode: str(formData.get("invite_code")),
  });
  if ("error" in result) {
    return result;
  }

  await sendWelcomeEmail(result.userId);
  await sendVerificationEmail(result.userId);

  try {
    // Laravel signs the new user in and sends them to the verification notice.
    await signIn("credentials", { email, password, redirectTo: "/email/verify" });
    return null;
  } catch (error) {
    if (error instanceof AuthError) {
      return { error: "Account created — please sign in." };
    }
    throw error; // NEXT_REDIRECT on success
  }
}
